import React, { useState, useEffect } from 'react';

// ============================================
// LOCALSTORAGE HELPER FUNCTIONS
// ============================================

/**
 * Load reviews from LocalStorage
 * Returns array of reviews or null if not found
 */
const loadReviewsFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem('company_reviews');
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    console.error('Error loading reviews from LocalStorage:', error);
    return null;
  }
};

/**
 * Save reviews to LocalStorage
 * @param {Array} reviews - Array of review objects
 */
const saveReviewsToLocalStorage = (reviews) => {
  try {
    localStorage.setItem('company_reviews', JSON.stringify(reviews));
    return true;
  } catch (error) {
    console.error('Error saving reviews to LocalStorage:', error);
    return false;
  }
};

// ============================================
// DEFAULT FAKE DATA WITH COMPANY NAMES
// ============================================

const defaultReviews = [
  {
    id: 1,
    companyName: 'TechCorp Solutions',
    rating: 5,
    type: 'positive',
    comment: 'Great culture and supportive team! The management really cares about employee well-being.',
    timestamp: '2024-10-01',
    comments: [
      {
        id: 101,
        text: 'I totally agree! Been working there for 2 years and it\'s been amazing.',
        timestamp: '2024-10-02'
      },
      {
        id: 102,
        text: 'Thanks for sharing! This is very helpful for my job search.',
        timestamp: '2024-10-03'
      }
    ]
  },
  {
    id: 2,
    companyName: 'Innovate Labs',
    rating: 3,
    type: 'negative',
    comment: 'Management needs improvement. Communication between departments is lacking.',
    timestamp: '2024-10-05',
    comments: [
      {
        id: 201,
        text: 'I had a similar experience during my interview process.',
        timestamp: '2024-10-06'
      }
    ]
  },
  {
    id: 3,
    companyName: 'Design Studio Pro',
    rating: 4,
    type: 'positive',
    comment: 'Decent benefits and flexible hours. Work-life balance is pretty good here.',
    timestamp: '2024-10-10',
    comments: []
  },
  {
    id: 4,
    companyName: 'TechCorp Solutions',
    rating: 4,
    type: 'positive',
    comment: 'Solid company with good career growth opportunities. Great learning environment.',
    timestamp: '2024-10-15',
    comments: [
      {
        id: 401,
        text: 'What technologies do they use there?',
        timestamp: '2024-10-16'
      }
    ]
  },
  {
    id: 5,
    companyName: 'Innovate Labs',
    rating: 5,
    type: 'positive',
    comment: 'Amazing startup culture! Fast-paced environment with lots of autonomy.',
    timestamp: '2024-10-20',
    comments: []
  }
];

// Fake company list for dropdown
const companyList = [
  'TechCorp Solutions',
  'Innovate Labs',
  'Design Studio Pro',
  'Global Systems Inc',
  'Creative Minds Agency',
  'Data Dynamics Corp',
  'Future Tech Labs',
  'Smart Solutions Group'
];

// ============================================
// STAR RATING COMPONENT
// ============================================

/**
 * StarRating Component
 * Displays star rating (filled or empty)
 * @param {number} rating - Rating value (1-5)
 * @param {function} onRatingChange - Callback for rating selection (optional)
 * @param {boolean} interactive - Whether stars are clickable
 */
const StarRating = ({ rating, onRatingChange, interactive = false }) => {
  const [hoverRating, setHoverRating] = useState(0);

  const handleClick = (value) => {
    if (interactive && onRatingChange) {
      onRatingChange(value);
    }
  };

  const handleMouseEnter = (value) => {
    if (interactive) {
      setHoverRating(value);
    }
  };

  const handleMouseLeave = () => {
    if (interactive) {
      setHoverRating(0);
    }
  };

  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((value) => {
        const isFilled = value <= (hoverRating || rating);
        return (
          <span
            key={value}
            onClick={() => handleClick(value)}
            onMouseEnter={() => handleMouseEnter(value)}
            onMouseLeave={handleMouseLeave}
            className={`text-2xl cursor-${interactive ? 'pointer' : 'default'} transition-colors ${
              isFilled ? 'text-yellow-400' : 'text-gray-300'
            } ${interactive ? 'hover:scale-110' : ''}`}
            style={{ transition: 'all 0.2s' }}
          >
            {isFilled ? '★' : '☆'}
          </span>
        );
      })}
    </div>
  );
};

// ============================================
// REVIEW CARD COMPONENT
// ============================================

/**
 * ReviewCard Component
 * Displays a single review with comments
 */
const ReviewCard = ({ review, onAddComment }) => {
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showAllComments, setShowAllComments] = useState(false);

  const isPositive = review.type === 'positive';
  const comments = review.comments || [];
  const commentsToShow = showAllComments ? comments : comments.slice(0, 2);

  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (commentText.trim().length < 5) {
      alert('Comment must be at least 5 characters long.');
      return;
    }

    onAddComment(review.id, commentText.trim());
    setCommentText('');
    setShowCommentForm(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 border-l-4 border-gradient hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] border border-gray-100">
      {/* Company Name Badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-2">
            <span className="text-2xl">🏢</span>
          </div>
          <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{review.companyName}</h3>
        </div>
        <span
          className={`px-4 py-2 rounded-full text-sm font-semibold shadow-md ${
            isPositive
              ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
              : 'bg-gradient-to-r from-red-500 to-pink-600 text-white'
          }`}
        >
          {isPositive ? '✓ Positive' : '✗ Negative'}
        </span>
      </div>

      {/* Rating */}
      <div className="mb-4 flex items-center space-x-2">
        <StarRating rating={review.rating} interactive={false} />
        <span className="text-sm font-semibold text-gray-600">({review.rating}/5)</span>
      </div>

      {/* Comment */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-4 mb-4">
        <p className="text-gray-700 text-base leading-relaxed italic">
          "{review.comment}"
        </p>
      </div>

      {/* Footer: Timestamp */}
      <div className="flex items-center justify-between text-sm text-gray-500 mb-4 pb-4 border-b border-gray-200">
        <div className="flex items-center space-x-2 bg-gray-50 px-3 py-1 rounded-lg">
          <span>📅</span>
          <span className="font-medium">{review.timestamp}</span>
        </div>
        <span className="text-gray-400 italic">Posted anonymously</span>
      </div>

      {/* Comments Section */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-3">
          <h4 className="text-sm font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent flex items-center">
            <span className="mr-2">💬</span>
            Comments ({review.comments.length})
          </h4>
          <button
            onClick={() => setShowCommentForm(!showCommentForm)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-lg text-sm font-semibold hover:from-purple-600 hover:to-pink-600 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
          >
            {showCommentForm ? '✕ Cancel' : '+ Add Comment'}
          </button>
        </div>

        {/* Comment Form */}
        {showCommentForm && (
          <form onSubmit={handleSubmitComment} className="mb-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border-2 border-purple-200">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="✍️ Write your comment anonymously..."
              className="w-full px-4 py-3 border-2 border-purple-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none shadow-sm"
              rows="3"
              required
            ></textarea>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs font-semibold text-purple-600 bg-white px-3 py-1 rounded-lg">
                {commentText.length} characters (min 5)
              </span>
              <button
                type="submit"
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-5 py-2 rounded-lg text-sm font-bold hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
              >
                💬 Post Comment
              </button>
            </div>
          </form>
        )}

        {/* Comments List */}
        {comments.length > 0 && (
          <div className="space-y-3">
            {commentsToShow.map((comment) => (
              <div key={comment.id} className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 rounded-xl border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-gray-700 text-sm mb-2 leading-relaxed">{comment.text}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="bg-white px-2 py-1 rounded-lg text-gray-600 font-medium">📅 {comment.timestamp}</span>
                  <span className="text-gray-500 italic">Anonymous</span>
                </div>
              </div>
            ))}

            {/* Show More/Less Button */}
            {comments.length > 2 && (
              <button
                onClick={() => setShowAllComments(!showAllComments)}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2 rounded-lg text-sm font-semibold hover:from-blue-600 hover:to-purple-600 shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all"
              >
                {showAllComments
                  ? '▲ Show Less'
                  : `▼ Show ${comments.length - 2} More Comments`}
              </button>
            )}
          </div>
        )}

        {comments.length === 0 && !showCommentForm && (
          <div className="text-center py-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
            <p className="text-gray-400 text-sm italic">💭 No comments yet. Be the first!</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT: COMPANY REVIEWS
// ============================================

const CompanyReviews = () => {
  // State management
  const [reviews, setReviews] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    companyName: '',
    rating: 5,
    type: 'positive',
    comment: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // ============================================
  // LOAD REVIEWS ON MOUNT
  // ============================================
  useEffect(() => {
    // Try to load from LocalStorage
    let loadedReviews = loadReviewsFromLocalStorage();

    // If no reviews exist, seed with default data
    if (!loadedReviews || loadedReviews.length === 0) {
      loadedReviews = defaultReviews;
      saveReviewsToLocalStorage(loadedReviews);
    }

    // Sort by timestamp (newest first)
    const sortedReviews = loadedReviews.sort((a, b) => {
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    setReviews(sortedReviews);
  }, []);

  // ============================================
  // HANDLE FORM INPUT CHANGES
  // ============================================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleRatingChange = (newRating) => {
    setFormData({
      ...formData,
      rating: newRating
    });
  };

  // ============================================
  // HANDLE ADD COMMENT TO REVIEW
  // ============================================
  const handleAddComment = (reviewId, commentText) => {
    const newComment = {
      id: Date.now(),
      text: commentText,
      timestamp: new Date().toISOString().split('T')[0]
    };

    const updatedReviews = reviews.map((review) => {
      if (review.id === reviewId) {
        return {
          ...review,
          comments: [...(review.comments || []), newComment]
        };
      }
      return review;
    });

    setReviews(updatedReviews);
    saveReviewsToLocalStorage(updatedReviews);
  };

  // ============================================
  // HANDLE FORM SUBMISSION
  // ============================================
  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    // Validation
    if (!formData.companyName) {
      setErrorMessage('Please select a company.');
      return;
    }

    if (!formData.comment.trim()) {
      setErrorMessage('Please write a comment before submitting.');
      return;
    }

    if (formData.comment.trim().length < 10) {
      setErrorMessage('Comment must be at least 10 characters long.');
      return;
    }

    // Create new review
    const newReview = {
      id: Date.now(),
      companyName: formData.companyName,
      rating: formData.rating,
      type: formData.type,
      comment: formData.comment.trim(),
      timestamp: new Date().toISOString().split('T')[0],
      comments: [] // Initialize empty comments array
    };

    // Add to reviews array (newest first)
    const updatedReviews = [newReview, ...reviews];

    // Save to LocalStorage
    const saved = saveReviewsToLocalStorage(updatedReviews);

    if (saved) {
      setReviews(updatedReviews);
      setSuccessMessage('✓ Review submitted successfully!');

      // Reset form
      setFormData({
        companyName: '',
        rating: 5,
        type: 'positive',
        comment: ''
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } else {
      setErrorMessage('Failed to save review. Please try again.');
    }
  };

  // ============================================
  // CALCULATE STATISTICS
  // ============================================
  // Filter reviews based on search query
  const filteredReviews = reviews.filter((review) => {
    if (!searchQuery.trim()) return true;
    return review.companyName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const totalReviews = filteredReviews.length;
  const averageRating =
    totalReviews > 0
      ? (filteredReviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews).toFixed(1)
      : 0;
  const positiveCount = filteredReviews.filter((r) => r.type === 'positive').length;
  const negativeCount = filteredReviews.filter((r) => r.type === 'negative').length;
  const totalComments = filteredReviews.reduce((sum, r) => sum + (r.comments?.length || 0), 0);

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50 to-pink-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white rounded-2xl shadow-2xl p-8 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24"></div>
          <div className="relative z-10">
            <div className="flex items-center mb-3">
              <span className="text-5xl mr-4">⭐</span>
              <h1 className="text-4xl font-bold">Anonymous Company Reviews</h1>
            </div>
            <p className="text-purple-100 text-lg">
              Share and read honest reviews about companies - completely anonymous
            </p>
          </div>
        </div>

        {/* Statistics Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl shadow-xl p-6 text-center text-white transform hover:scale-105 transition-all duration-300">
            <div className="text-4xl font-bold mb-1">{totalReviews}</div>
            <div className="text-blue-100 text-sm font-medium">Total Reviews</div>
          </div>
          <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl shadow-xl p-6 text-center text-white transform hover:scale-105 transition-all duration-300">
            <div className="text-4xl font-bold mb-1">{averageRating}</div>
            <div className="text-yellow-100 text-sm font-medium">Average Rating</div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-xl p-6 text-center text-white transform hover:scale-105 transition-all duration-300">
            <div className="text-4xl font-bold mb-1">{positiveCount}</div>
            <div className="text-green-100 text-sm font-medium">Positive</div>
          </div>
          <div className="bg-gradient-to-br from-red-500 to-pink-600 rounded-xl shadow-xl p-6 text-center text-white transform hover:scale-105 transition-all duration-300">
            <div className="text-4xl font-bold mb-1">{negativeCount}</div>
            <div className="text-red-100 text-sm font-medium">Negative</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-xl p-6 text-center text-white transform hover:scale-105 transition-all duration-300">
            <div className="text-4xl font-bold mb-1">{totalComments}</div>
            <div className="text-purple-100 text-sm font-medium">Comments</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Review Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-2xl p-8 sticky top-4 border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-3 mr-3">
                  <span className="text-3xl">✍️</span>
                </div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Write a Review
                </h2>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-xl mb-6 border-l-4 border-blue-500">
                <p className="text-gray-700 text-sm font-medium flex items-center">
                  <span className="text-xl mr-2">🔒</span>
                  Your identity is completely anonymous
                </p>
              </div>

              {/* Success Message */}
              {successMessage && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 text-green-700 px-4 py-3 rounded-xl mb-4 font-medium">
                  {successMessage}
                </div>
              )}

              {/* Error Message */}
              {errorMessage && (
                <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4 font-medium">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Company Name Input with Suggestions */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    list="company-suggestions"
                    placeholder="Type or select a company..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <datalist id="company-suggestions">
                    {companyList.map((company, index) => (
                      <option key={index} value={company} />
                    ))}
                  </datalist>
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Start typing to see suggestions or enter your own
                  </p>
                </div>

                {/* Star Rating Selector */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Rating *
                  </label>
                  <StarRating
                    rating={formData.rating}
                    onRatingChange={handleRatingChange}
                    interactive={true}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Click to select ({formData.rating}/5 stars)
                  </p>
                </div>

                {/* Review Type Selector */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Review Type *
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="type"
                        value="positive"
                        checked={formData.type === 'positive'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-green-600 focus:ring-green-500"
                      />
                      <span className="ml-2 text-gray-700">
                        ✓ Positive Experience
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="type"
                        value="negative"
                        checked={formData.type === 'negative'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-gray-700">
                        ✗ Negative Experience
                      </span>
                    </label>
                  </div>
                </div>

                {/* Comment Textarea */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Your Review *
                  </label>
                  <textarea
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                    rows="5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Share your experience... (minimum 10 characters)"
                    required
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.comment.length} characters
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  ✨ Submit Anonymous Review
                </button>

                {/* Anonymous Notice */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl text-center border border-indigo-200">
                  <p className="text-sm text-indigo-800 font-medium">
                    🔒 No personal information is collected or displayed
                  </p>
                </div>
              </form>
            </div>
          </div>

          {/* Right Column: Reviews List */}
          <div className="lg:col-span-2">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="🔍 Search reviews by company name..."
                  className="w-full px-4 py-4 pl-14 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-md transition-all"
                />
                <span className="absolute left-4 top-4 text-gray-400 text-2xl">
                  🔍
                </span>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 font-bold text-xl"
                  >
                    ✕
                  </button>
                )}
              </div>
              {searchQuery && (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-xl mt-3 border-l-4 border-blue-500">
                  <p className="text-sm text-gray-700 font-medium">
                    Showing results for: <span className="font-bold text-blue-600">{searchQuery}</span>
                  </p>
                </div>
              )}
            </div>

            <div className="mb-6 flex items-center justify-between bg-white rounded-xl p-4 shadow-md border border-gray-100">
              <div className="flex items-center">
                <span className="text-3xl mr-3">📋</span>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {searchQuery ? `Search Results (${totalReviews})` : `All Reviews (${totalReviews})`}
                </h2>
              </div>
              <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                📅 Newest first
              </span>
            </div>

            {/* Reviews List */}
            {filteredReviews.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-xl p-16 text-center border-2 border-dashed border-gray-300">
                <div className="text-6xl mb-4">{searchQuery ? '🔍' : '📝'}</div>
                <p className="text-gray-600 text-lg">
                  {searchQuery
                    ? `No reviews found for "${searchQuery}". Try a different search.`
                    : 'No reviews yet. Be the first to share your experience!'}
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredReviews.map((review) => (
                  <ReviewCard
                    key={review.id}
                    review={review}
                    onAddComment={handleAddComment}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyReviews;
